import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import WithMobileHeader from '@/components/WithMobileHeader';
import { useDimensions } from '@/hooks/useDimensions';
import { useSessionStore } from '@/hooks/useSessionStore';
import { getStyleFilterArtistDetail } from '@/service/filter';
import { cn } from '@/utils';
import { useRequest } from 'ahooks';
import { addDays, addMonths, format, subMonths } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import StyleFilterCard from './components/StyleFilterCard';
import { useFilterAnalyticsData } from './hooks/useFilterAnalyticsData';
import { CloseFilledIcon, CloseIcon } from '@/components/Icons';

const StyleFiltersPage: React.FC = () => {
  const { isMobileOrPad } = useDimensions();

  const sessionStore = useSessionStore();
  const artist = sessionStore?.session?.artist;
  const artistId = artist?.id || '00000000-0000-0000-0000-000000000000';

  const { date, setDate, processedStatsData } = useFilterAnalyticsData(true);

  const { data: getStyleFilterArtistDetailData } = useRequest(() => getStyleFilterArtistDetail(artistId as string), {
    refreshDeps: [artistId],
    ready: !!artistId,
  });

  const filterData = useMemo(() => {
    return {
      public: getStyleFilterArtistDetailData?.data?.sf_list.filter((item) => item.is_published),
      private: getStyleFilterArtistDetailData?.data?.sf_list.filter((item) => !item.is_published),
    };
  }, [getStyleFilterArtistDetailData]);

  if (isMobileOrPad) {
    return (
      <WithMobileHeader>
        <div className="flex h-screen bg-black text-white">
          <div className="flex-1 overflow-y-auto">
            <header className="px-3 pt-10 pb-3 border-b border-[#242425]">
              <div className="justify-end text-[24px] text-[#71717A] font-normal leading-[32px]">StyleFilters™️</div>
              <div className="justify-end text-[24px] font-normal leading-[32px]">{getStyleFilterArtistDetailData?.data?.name}</div>
            </header>

            <main className="px-3 py-4">
              <section className="mb-12 flex flex-col gap-6">
                {/* <h3 className="text-[22px] leading-[32px] mb-6">Public StyleFilters</h3> */}
                {filterData?.public?.map((item) => (
                  <StyleFilterCard
                    key={item.id}
                    id={item.id}
                    title={item.name}
                    dateCreated={item.created_at}
                    datePublished={item?.published_at}
                    isPublic={true}
                    album_cover={item?.album_cover}
                  />
                ))}
              </section>

              <section>
                {!!filterData?.private?.length && <h3 className="text-[22px] leading-[32px] mb-6">Private StyleFilters</h3>}
                <div className="flex flex-col gap-6">
                  {filterData?.private?.map((item) => (
                    <StyleFilterCard
                      id={item.id}
                      title={item.name}
                      dateCreated={item.created_at}
                      datePublished={item?.published_at}
                      isPublic={false}
                      album_cover={item?.album_cover}
                    />
                  ))}
                </div>
              </section>
            </main>
          </div>
        </div>
      </WithMobileHeader>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#111112] text-white">
      <div className="flex-1 overflow-y-auto">
        <header className="px-12 pt-12 pb-6 border-b border-[#242425]">
          <div className="justify-end text-[32px] text-[#71717A] font-normal leading-[32px]">StyleFilters™️</div>
          <div className="flex justify-between items-center">
            <div className="justify-end text-[32px] font-normal leading-[32px]">{getStyleFilterArtistDetailData?.data?.name}</div>
            <div className="">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={'outline'}
                    className={cn(
                      'p-3 w-auto justify-start text-left font-normal !bg-[#111112] border-[#2a2a2b] text-white hover:opacity-80 hover:text-white',
                      !date && 'text-white',
                    )}
                  >
                    <ChevronLeft
                      className="mr-3 h-4 w-4"
                      onClick={() => {
                        if (setDate) {
                          if (!date) {
                            setDate({ from: subMonths(new Date(), 1), to: new Date() });
                          } else {
                            setDate({ from: subMonths(date.from as Date, 1), to: date.from as Date });
                          }
                        }
                      }}
                    />
                    <CalendarIcon className="mr-[10px] h-4 w-4" />
                    {date?.from ? (
                      date.to ? (
                        <>
                          {format(date.from, 'yyyy-MM-dd')} - {format(date.to, 'yyyy-MM-dd')}
                        </>
                      ) : (
                        format(date.from, 'yyyy-MM-dd')
                      )
                    ) : (
                      <span>All Time</span>
                    )}
                    <ChevronRight
                      className="ml-3 h-4 w-4"
                      onClick={() => {
                        if (setDate) {
                          if (!date) {
                            setDate({ from: addMonths(new Date(), 1), to: new Date() });
                          } else {
                            setDate({ from: addMonths(date.from as Date, 1), to: date.from as Date });
                          }
                        }
                      }}
                    />
                    {date && (
                      <CloseIcon
                        fill="#fff"
                        onClick={(e) => {
                          e.preventDefault();
                          setDate(undefined);
                        }}
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="flex w-auto flex-col space-y-2 p-2 bg-[#1a1a1b] border-[#2a2a2b] text-white">
                  <div className="rounded-md border border-[#2a2a2b]">
                    <Calendar
                      initialFocus
                      mode="range"
                      defaultMonth={date?.from}
                      selected={date}
                      onSelect={setDate}
                      numberOfMonths={2}
                      classNames={{
                        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                        month: 'space-y-4',
                        caption: 'flex justify-center pt-1 relative items-center text-white',
                        caption_label: 'text-sm font-medium text-white',
                        nav: 'space-x-1 flex items-center',
                        nav_button: 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white',
                        nav_button_previous: 'absolute left-1',
                        nav_button_next: 'absolute right-1',
                        table: 'w-full border-collapse space-y-1',
                        head_row: 'flex',
                        head_cell: 'text-white/60 rounded-md w-9 font-normal text-[0.8rem]',
                        row: 'flex w-full mt-2',
                        cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-[#2a2a2b]/50 [&:has([aria-selected])]:bg-[#2a2a2b] first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
                        day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-white hover:bg-[#2a2a2b]',
                        day_range_end: 'day-range-end',
                        day_selected: 'bg-[#ff5b27] text-white hover:bg-[#ff5b27] hover:text-white focus:bg-[#ff5b27] focus:text-white',
                        day_today: 'bg-[#2a2a2b] text-white',
                        day_outside: 'day-outside text-white/60 aria-selected:bg-[#2a2a2b]/50 aria-selected:text-white/60',
                        day_disabled: 'text-white/40 opacity-50',
                        day_range_middle: 'aria-selected:bg-[#2a2a2b] aria-selected:text-white',
                        day_hidden: 'invisible',
                      }}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="flex flex-row mt-12">
            {processedStatsData.map((stat, index) => (
              <div key={index} className={cn('flex flex-col items-start gap-0.5 py-4 flex-1')}>
                <div className="text-[16px] self-stretch font-normal text-[#71717A] text-base tracking-[-0.64px] leading-4">
                  Total&nbsp;{stat.label}
                </div>
                <div className="mt-3 text-[26px] self-stretch font-normal text-white text-base tracking-[-0.64px] leading-4">
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        </header>
        <main className="p-12">
          <section className="mb-12">
            {!!filterData?.public?.length && <h3 className="text-[22px] leading-[32px] mb-6">Public StyleFilters</h3>}
            <div className="flex flex-col gap-6">
              {filterData?.public?.map((item) => (
                <StyleFilterCard
                  key={item.id}
                  id={item.id}
                  title={item.name}
                  dateCreated={item.created_at}
                  datePublished={item?.published_at}
                  isPublic={item?.is_published}
                  album_cover={item?.album_cover}
                />
              ))}
            </div>
          </section>

          <section>
            {!!filterData?.private?.length && <h3 className="text-[22px] leading-[32px] mb-6">Private StyleFilters</h3>}
            <div className="flex flex-col gap-6">
              {filterData?.private?.map((item) => (
                <StyleFilterCard
                  key={item.id}
                  id={item.id}
                  title={item.name}
                  dateCreated={item.created_at}
                  datePublished={item?.published_at}
                  isPublic={item?.is_published}
                  album_cover={item?.album_cover}
                />
              ))}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export function DatePickerWithRange({ className }: React.HTMLAttributes<HTMLDivElement>) {
  const [date, setDate] = React.useState<DateRange | undefined>({
    from: new Date(2022, 0, 20),
    to: addDays(new Date(2022, 0, 20), 20),
  });

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            className={cn('w-[300px] justify-start text-left font-normal', !date && 'text-muted-foreground')}
          >
            <CalendarIcon />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, 'LLL dd, y')} - {format(date.to, 'LLL dd, y')}
                </>
              ) : (
                format(date.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar initialFocus mode="range" defaultMonth={date?.from} selected={date} onSelect={setDate} numberOfMonths={2} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default StyleFiltersPage;
